import type {ReactNode} from 'react';
import React, {useCallback, useState} from 'react';

import AnnotationPopover from './AnnotationPopover';
import {useAnnotationModeStore} from './AnnotationModeStore';
import {selectAnnotationType, selectHighlightColor} from './annotationSelectors';
import type {AnnotationAnchor} from './annotationTypes';
import {useAnnotations} from './useAnnotations';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';

import './annotations-cursor.scss';
import './annotations-highlights.scss';

interface AnnotatableContentProps {
  regionId: string | null;
  /** Language of the rendered text ('es' when translated); null = original. */
  lang?: string | null;
  children: ReactNode;
}

/**
 * Wraps children so selections inside them become annotations (see
 * `useAnnotations`). Anchors index the region's normalized visible text
 * (annotationTextProjection.ts), not the source markdown.
 */
export default function AnnotatableContent({
  regionId,
  lang = null,
  children
}: AnnotatableContentProps) {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();

  if (!isAnnotationModeAvailable || !regionId) {
    return <>{children}</>;
  }

  return (
    <AnnotatableRegion regionId={regionId} lang={lang}>
      {children}
    </AnnotatableRegion>
  );
}

function AnnotatableRegion({
  regionId,
  lang,
  children
}: {
  regionId: string;
  lang: string | null;
  children: ReactNode;
}) {
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const {captureAnnotation, getAnnotationAtPoint, containerRef} = useAnnotations(regionId, lang);
  const setHighlightColor = useAnnotationModeStore((state) => state.setHighlightColor);
  const setAnnotationUnderline = useAnnotationModeStore((state) => state.setAnnotationUnderline);
  const removeAnnotation = useAnnotationModeStore((state) => state.removeAnnotation);
  const [activeAnnotation, setActiveAnnotation] = useState<AnnotationAnchor | null>(null);
  // The active annotation's own color and type drive the popover's selection.
  const activeColor = useAnnotationModeStore((state) =>
    selectHighlightColor(state, regionId, activeAnnotation?.id)
  );
  const activeType = useAnnotationModeStore((state) =>
    selectAnnotationType(state, regionId, activeAnnotation?.id)
  );

  const handleMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isAnnotationModeOn) {
        return;
      }
      const captured = captureAnnotation();
      if (captured) {
        setActiveAnnotation(captured);
        return;
      }
      // Don't hijack a real text selection (e.g. select-to-copy).
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        return;
      }
      // A plain click on an existing annotation opens its style editor.
      setActiveAnnotation(getAnnotationAtPoint(event.clientX, event.clientY));
    },
    [isAnnotationModeOn, captureAnnotation, getAnnotationAtPoint]
  );

  return (
    <>
      <div
        ref={containerRef}
        className={isAnnotationModeOn ? 'cursor-highlighter' : undefined}
        onMouseUp={handleMouseUp}
      >
        {children}
      </div>
      {activeAnnotation && activeColor && activeType && (
        <AnnotationPopover
          anchorRect={activeAnnotation.rect}
          selectedColor={activeColor}
          selectedType={activeType}
          onSelectColor={(color) => setHighlightColor(regionId, activeAnnotation.id, color)}
          onSelectUnderline={() => setAnnotationUnderline(regionId, activeAnnotation.id)}
          onRemove={() => {
            removeAnnotation(regionId, activeAnnotation.id);
            setActiveAnnotation(null);
          }}
          onDismiss={() => setActiveAnnotation(null)}
        />
      )}
    </>
  );
}
